/**
 * The portal's own route guard.
 *
 * It deliberately does NOT use PrivateRoute/OnlyPublicRoute from Routes.jsx —
 * those key off the ADMIN session and redirect to /login. A driver has no
 * dashboard credentials, so a missing portal session renders the
 * "ask for a new code" screen in place, never a redirect.
 *
 * It also owns the portal's keep-alive: the id token is renewed on a timer and
 * whenever the app returns to the foreground, so a driver who last opened the
 * app yesterday still lands straight on today's work.
 *
 * `requireKind` ('operator' | 'driver' | 'dispatcher') keeps each login on
 * its own screens: a driver who opens an operator URL is sent to `/portal`
 * (their day), an operator who opens a driver-only URL to
 * `/portal/operator`, a dispatcher to its board. This is navigation, not
 * security — every endpoint refuses the wrong kind of login with 403 on its
 * own, in both directions.
 *
 * `wide` and `title` go to the signed-in PortalShell (the Operator Admin's
 * desktop width and English tab title). The gate keeps the phone column.
 */
import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  driverPushApi,
  ensurePortalToken,
  getAssumedDriver,
  getPortalPrincipal,
  hasPortalSession,
  onPortalSessionEnded,
  stopAssumingDriver,
} from '../../Services/PortalApi';
import { syncSubscription } from '../../Services/PushNotifications';
import { officePushApi } from '../../Services/NotificationService';
import { PORTAL_USER_KINDS } from '../../config/config';
import PortalShell from './Components/PortalShell';
import PortalGate from './Components/PortalGate';
import AssumedDriverBar from './Components/AssumedDriverBar';
import { BOARD_DID_NOT_OPEN } from './StaffLogin';
import usePortalPrincipal from './Operator/usePortalPrincipal';

const HOME_FOR_KIND = {
  [PORTAL_USER_KINDS.OPERATOR]: '/portal/operator',
  [PORTAL_USER_KINDS.DRIVER]: '/portal',
  [PORTAL_USER_KINDS.DISPATCHER]: '/portal/dispatcher',
};

/** Where each login's phone registers itself for push. An operator has none. */
const PUSH_API_FOR_KIND = {
  [PORTAL_USER_KINDS.DRIVER]: driverPushApi,
  [PORTAL_USER_KINDS.DISPATCHER]: officePushApi,
};

/**
 * A session the API would not accept.
 *
 * Rare, and no longer about permissions: any Pickup Hungary account reaches
 * the board, so what is left is a token the API refuses to resolve. Says so
 * plainly and offers the one thing that might help — signing in again —
 * rather than a spinner or a board that mounts and fills with errors.
 */
function BoardDidNotOpen() {
  return (
    <div className="pp-gate">
      <h1 className="pp-gate__title">The board could not open</h1>
      <p className="pp-gate__body">{BOARD_DID_NOT_OPEN}</p>
      <p className="pp-gate__body pp-gate__staff">
        <Link to="/portal/login">Sign in again</Link>
      </p>
    </div>
  );
}

/**
 * The impersonation bar, wherever an administrator is acting as a driver.
 *
 * Here rather than in each screen because it has to be on ALL of them: the
 * day list, a trip, the expense sheet.
 *
 * It reads the same stored driver the request interceptor does, and that is
 * the point — the stored id IS what puts `x-assume-driver` on every call, so
 * "the bar is up" and "the API is treating me as this driver" cannot come
 * apart. It is sessionStorage, so it survives a reload of this tab and dies
 * with it; and when storage is unavailable, nothing is stored, no header is
 * sent, and there is correctly nothing to warn about.
 *
 * No /portal/session call of its own: the guard below already makes that one,
 * and a banner is not worth a second.
 */
function AssumedBanner() {
  const navigate = useNavigate();
  const assumed = getAssumedDriver();

  if (!assumed) return null;

  const stop = () => {
    stopAssumingDriver();
    navigate(HOME_FOR_KIND[PORTAL_USER_KINDS.DISPATCHER], { replace: true });
  };

  return <AssumedDriverBar driverName={assumed.name} onStop={stop} />;
}

function KindGate({ requireKind, children }) {
  const { kind, loading, error } = usePortalPrincipal();

  if (loading) return <div className="pp-state" role="status">Loading…</div>;

  // The API refused to say who this is. No permission gates the board, so
  // this is not "you are not allowed" — it is a token the API would not
  // resolve, and every portal endpoint will answer the same. Say so plainly
  // rather than letting a board mount and fill with errors.
  //
  // Not the driver gate: that screen offers a WhatsApp message asking the
  // office for a new link, which would be useless advice here — nobody can
  // send a staff member one.
  if (error?.response?.status === 403) return <BoardDidNotOpen />;

  // Unknown kind for any other reason (offline, a 5xx): let the screen load —
  // its API calls are authorised server-side and it renders their errors.
  if (kind && kind !== requireKind) {
    return <Navigate to={HOME_FOR_KIND[kind] || '/portal'} replace />;
  }

  return children;
}

// Well inside the shortest id-token lifetime this guard sees — the portal
// pool's hour, for a driver or operator — so an idle app is always current.
// A staff session's token lives a day and renews on its own schedule
// (SessionRenewal reads the token's `exp`), so this simply finds nothing to
// do until it is close.
const KEEPALIVE_MS = 10 * 60 * 1000;

export default function PortalRoute({
  children, requireKind = null, wide = false, title,
}) {
  const [signedIn, setSignedIn] = useState(() => hasPortalSession());

  useEffect(() => {
    // A refresh that fails for good clears storage and fires this, which flips
    // the screen to the gate instead of leaving a dead spinner.
    const unsubscribe = onPortalSessionEnded(() => setSignedIn(false));
    return unsubscribe;
  }, []);

  // A browser can rotate or drop a push subscription without telling anyone
  // (a reinstalled Home Screen app, cleared site data, a rotated endpoint).
  // Re-posting the live one on load keeps the server's copy honest; the
  // server keys rows by endpoint, so an unchanged one is a no-op. Silent and
  // best-effort.
  //
  // WHICH api DEPENDS ON WHO IS SIGNED IN. A driver's subscription belongs
  // to the portal's own endpoint; a dispatcher's is a DASHBOARD device
  // (`officePushApi`, the same rows the office's own settings screen
  // manages), because what they receive is the dashboard bell's twin. An
  // operator login has no push of its own and posts nothing.
  useEffect(() => {
    if (!signedIn) return;

    const api = PUSH_API_FOR_KIND[getPortalPrincipal()?.kind];

    if (api) syncSubscription(api);
  }, [signedIn]);

  useEffect(() => {
    if (!signedIn) return undefined;

    let cancelled = false;
    const tick = async () => {
      const token = await ensurePortalToken();
      if (!cancelled && !token && !hasPortalSession()) setSignedIn(false);
    };

    tick();
    const timer = setInterval(tick, KEEPALIVE_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [signedIn]);

  if (!signedIn) {
    return (
      <PortalShell dark>
        <PortalGate />
      </PortalShell>
    );
  }

  return (
    <PortalShell wide={wide} title={title}>
      <AssumedBanner />
      {requireKind ? <KindGate requireKind={requireKind}>{children}</KindGate> : children}
    </PortalShell>
  );
}
